/* eslint-disable class-methods-use-this */
import {NAVIGATION_REDUCER} from 'Store/reducer';
import {axaStore} from 'Store/index';
import {connectedBtn} from './header.tpl';

/**
 * @typedef {object} toggler
 * @property {string} activeClassName - The active class name.
 * @property {HTMLElement} domTarget - The dom target.
 * @property {HTMLElement} domTrigger - The dom trigger.
 * @property {string} toggleClassName - The toggle class name.
 */

export default class Header {
    constructor(domElement) {
        if(!domElement) {
            throw new Error('domElement is undefined');
        }

        if(domElement.dataset.headerInitialized === 'true') {
            throw new Error('Module already initialized');
        }

        domElement.dataset.headerInitialized = 'true';

        this.domElement = domElement;
        this.html = document.querySelector('html');
        this.triggerList = [...document.querySelectorAll('[data-header-trigger]')];
        this.IsConnectedHeader();

        this.initHeader();
    }

    HideEspaceClientButton({specificUniverse, currentUniverse, espaceClientLink}, connectedButton) {
        const specificUniverseList = JSON.parse(specificUniverse);

        specificUniverseList.forEach(specificUniverseItem => {
            if(specificUniverseItem.universe === currentUniverse) {
                connectedButton.firstElementChild.href = specificUniverseItem.specificEspaceClientLink || espaceClientLink;
                specificUniverseItem.hideEspaceClient.length !== 0 && connectedButton.classList.add('hidden');
            }
        });
    }

    IsConnectedHeader() {
        const connectedButton = this.domElement.querySelector('[data-btn-status]');
        const {path, ressource, picto, specificUniverse, currentUniverse, espaceClientLink} = connectedButton.dataset;

        // Hide espace client button for a specific universe
        this.HideEspaceClientButton({specificUniverse, currentUniverse, espaceClientLink}, connectedButton);

        if(path && ressource) {
            const unsubscribe = axaStore.subscribe(() => {
                const {isConnected} = axaStore.getState()[NAVIGATION_REDUCER].client;
                if(isConnected) {
                    connectedButton.innerHTML = connectedBtn(path, ressource, picto);
                    unsubscribe();
                }
            });
        }
    }

    /**
     * @description Get a list with all data to process toggle in header and init listeners.
     *
     */
    initHeader() {
        const togglerList = this.getTogglerList(this.triggerList);
        if(togglerList.length > 0) {
            this.initListeners(togglerList);
        }
    }

    /**
     * @description Returns a toggler list from trigger list with all data needed to toggle.
     * @param {Array} triggerList - Nodes list which have data-header-trigger attr.
     * @returns {toggler[]} - The toggler list.
     */
    getTogglerList(triggerList) {
        return triggerList.reduce((acc, domCurrentElement) => {
            const triggerClassName = domCurrentElement.dataset.headerTrigger;
            const activeClassName = domCurrentElement.dataset.headerActive;
            const domTrigger = this.domElement.querySelector(`.${triggerClassName}`);
            const toggleClassName = domTrigger.dataset.headerToggle;

            return [
                ...acc,
                {
                    activeClassName,
                    domTarget: domCurrentElement,
                    domTrigger,
                    toggleClassName
                }
            ];
        }, []);
    }

    /**
     * @description Add click into DOM togglers.
     * @param {toggler[]} togglerList - A list of togglers.
     */
    initListeners(togglerList) {
        togglerList.forEach(toggler => {
            const {domTrigger} = toggler;
            domTrigger.addEventListener('click', this.handleTogglerClick.bind(this, toggler));
        });
    }

    /**
     * @description Callback to click event on a toggler.
     * @param {toggler} toggler - Current toggler.
     * @param {Event} event - Event from listener.
     */
    handleTogglerClick(toggler, event) {
        event.preventDefault();
        event.stopPropagation();

        const {domTrigger} = toggler;
        const domOverlay = domTrigger.querySelector('.overlay-js');

        this.toggleClassNames(toggler);

        if(domOverlay) {
            this.toggleOverlay(domOverlay);
        } else {
            this.createOverlay(toggler);
        }
    }

    /**
     * @description Callback to click event on an overlay.
     * @param {toggler} toggler - Current toggler.
     * @param {HTMLElement} domOverlay - Current overlay Node.
     * @param {Event} event - Event from listener.
     */
    handleOverlayClick(toggler, domOverlay, event) {
        event.preventDefault();
        event.stopPropagation();

        this.toggleOverlay(domOverlay);
        this.toggleClassNames(toggler);
    }

    /**
     * @description Create overlay to close opened UI domElement.
     * @param {toggler} toggler - Current toggler.
     */
    createOverlay(toggler) {
        const domOverlay = this.configAndAppendOverlay(toggler);
        this.initOverlayListener(domOverlay, toggler);
    }

    /**
     * @description Config and append overlay.
     * @param {toggler} toggler - Current toggler.
     * @returns {HTMLElement} - Created overlay Node.
     */
    configAndAppendOverlay(toggler) {
        const {domTrigger} = toggler;
        const overlayAdditionalClassName = domTrigger.dataset.headerOverlayClass;
        const domOverlay = document.createElement('div');
        domOverlay.classList.add('overlay-js');

        if(overlayAdditionalClassName) {
            domOverlay.classList.add(overlayAdditionalClassName);
        }

        domTrigger.appendChild(domOverlay);

        return domOverlay;
    }

    /**
     * @description Toggle overflow on body if needed by current toggler.
     * @param {toggler} toggler - Current toggler.
     */
    toggleOverflow(toggler) {
        const {domTrigger} = toggler;
        const needOverflowHidden = domTrigger.dataset.headerOverflowHidden;
        const overflowHiddenClassName = 'ovh';

        if(needOverflowHidden) {
            this.html.classList.toggle(overflowHiddenClassName);
            document.body.classList.toggle(overflowHiddenClassName);
        }
    }

    /**
     * @description Toggle display of overlay.
     * @param {HTMLElement} domOverlay - Current overlay Node.
     */
    toggleOverlay(domOverlay) {
        domOverlay.classList.toggle('hidden');
    }

    /**
     * @description Init Listener on overlay.
     * @param {HTMLElement} domOverlay - Current overlay Node.
     * @param {toggler} toggler - Current toggler.
     */
    initOverlayListener(domOverlay, toggler) {
        domOverlay.addEventListener('click', this.handleOverlayClick.bind(this, toggler, domOverlay));
    }

    /**
     * @description Toggle CSS className to open and close UI Element.
     * @param {toggler} toggler - Current toggler.
     */
    toggleClassNames(toggler) {
        const {domTrigger, domTarget, activeClassName, toggleClassName} = toggler;

        this.toggleOverflow(toggler);

        domTarget.classList.toggle(activeClassName);
        if(toggleClassName) {
            domTrigger.classList.toggle(toggleClassName);
        }
    }
}
